import { Container, Grid, Heading, Text } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode } from 'react'
import { BASE_PATH } from '../../env'
import { PromotionType } from './constants'
import Image from 'next/image'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation, Trans } from '../../utils/convergence'
import { TermLink } from '..'
import { getNewUserPromoMerchants } from './utils'
import { useCrossRegionUserSignIn } from '../../state'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { useRoutePageName } from '../../config/router'
import { TrackOnMount } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'

interface PromotionProps {
  merchantId: number | undefined
  promotionType: PromotionType
}

interface NewUserPromoDetail {
  promoId: string
  amount: string
  startDate: string
  endDate: string
  currentDate: Date
  promoStartDate: Date
  promoEndDate: Date
}

export const Promotion: FunctionComponent<PromotionProps> = ({ merchantId, promotionType }) => {
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { isCrossRegion } = useCrossRegionUserSignIn()
  const currentPage = useRoutePageName()

  const newUserPromoMerchants = getNewUserPromoMerchants()
  const isMerchantNewUserPromo = promotionType === PromotionType.NEW_USER_PROMO && typeof newUserPromoMerchants[merchantId ?? ''] !== 'undefined'

  const { t } = useTranslation()
  const giftBoxSrc = [BASE_PATH, 'images/giftbox.svg'].join('/')

  const getNewUserPromoText = (): JSX.Element => {
    return <Trans i18nKey={'common:newUserPromo:body'} components={newUserPromoShowTerms} />
  }

  const getNewUserPromoDetails = (): NewUserPromoDetail => {
    const promoId = newUserPromoMerchants[merchantId ?? '']?.promoId ?? ''
    const amount = newUserPromoMerchants[merchantId ?? '']?.discountAmount ?? ''
    const startDate = newUserPromoMerchants[merchantId ?? '']?.startDate ?? ''
    const endDate = newUserPromoMerchants[merchantId ?? '']?.endDate ?? ''

    const currentDate = new Date()
    const promoStartDate = new Date(startDate)
    const promoEndDate = new Date(endDate)

    return {
      promoId,
      amount,
      startDate,
      endDate,
      currentDate,
      promoStartDate,
      promoEndDate,
    }
  }

  const renderNewUserPromo = (): ReactNode => {
    const { promoId, amount, startDate, endDate, currentDate, promoStartDate, promoEndDate } = getNewUserPromoDetails()

    if (isUndefinedOrEmpty(startDate) || isUndefinedOrEmpty(endDate)) {
      return null
    }
    if (currentDate < promoStartDate || currentDate > promoEndDate) {
      return null
    }

    return (
      <>
        <Container testNameSpace='promotion'>
          <Grid>
            <Grid.Item sm={9} md={9}>
              <Heading size={textSize}>{t('common:newUserPromo:title', { amount })}</Heading>
              <Text size={smallTextSize}>{getNewUserPromoText()}</Text>
            </Grid.Item>
            <Grid.Item>
              <Image
                src={giftBoxSrc}
                alt=''
                width='68'
                height='68'
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
            </Grid.Item>
          </Grid>
        </Container>
        <TrackOnMount eventName={TrackingEvent.VIEWED_PROMO} eventProperties={{ promoId, page: currentPage }} />
      </>
    )
  }

  const renderComponent = (): ReactNode => {
    if (isMerchantNewUserPromo) {
      if (isCrossRegion) {
        return null
      }
      return renderNewUserPromo()
    }
    return null
  }

  const newUserPromoShowTerms = {
    terms: (
      <TermLink
        key='new-user-promo-terms-button'
        href='#'
        onClick={() => {
          const { promoId } = getNewUserPromoDetails()
          window.open(`https://static.afterpay.com/modal/promo-${promoId}.html`, '_blank')
        }}
        target='_self'
        testNameSpace='new-user-promo-terms-button'
      />
    ),
  }

  return <>{renderComponent()}</>
}
